import React, { useRef, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { useNavigate } from "@tanstack/react-router"
import { useCommandState } from "cmdk"
import { IconCheckOutline18 as Check } from "nucleo-ui-outline-18"

import type { buttonVariantsType, PopoverProps } from "@productlane/ui"
import type { Tag } from "@productlane/zero"
import { ShortcutSymbols } from "@productlane/lib/src/shortcut-symbols"
import {
	Button,
	Popover,
	Shortcut,
	Tooltip,
	useBreakpoints,
} from "@productlane/ui"
import {
	Command,
	CommandAddNewItem,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@productlane/ui/src/components/internal/command"
import { DropdownMenuLabel } from "@productlane/ui/src/components/internal/dropdown"
import { TagFilledIcon } from "@productlane/ui/src/icons/project/tag-filled-icon"
import { queries } from "@productlane/zero/queries"

import type { PopoverContentProps } from "@radix-ui/react-popover"
import { useShortcut } from "@/lib"
import { analytics } from "@/lib/analytics"
import { getProjectIcon } from "@/lib/projects"
import { useRole } from "@/lib/useRole"
import { CreateTagDialog } from "../../../tags/create-tag-dialog"

interface ThreadTagSelectProps {
	tagIds: Array<string>
	onChange: (tagId: string, tagColor: string) => void
	className?: string
	side?: PopoverContentProps["side"]
	align?: PopoverContentProps["align"]
	closeOnSelect?: boolean
	modal?: PopoverProps["modal"]
	isHotkeyEnabled?: boolean
	onCloseAutoFocus?: PopoverProps["onCloseAutoFocus"]
	isDisabled?: boolean
	variant?: buttonVariantsType
	isOpen?: boolean
	onOpenChange?: (open: boolean) => void
	children?: React.ReactNode
}

export const ThreadTagSelect = React.memo(
	({
		onChange,
		tagIds,
		side,
		align,
		closeOnSelect = false,
		modal,
		isHotkeyEnabled = true,
		onCloseAutoFocus,
		isDisabled,
		variant = "dashed",
		isOpen: externalIsOpen,
		onOpenChange: externalOnOpenChange,
		children,
	}: ThreadTagSelectProps) => {
		const { role } = useRole()
		const { isMobile } = useBreakpoints()

		// On mobile, open below instead of to the side to stay within viewport
		const responsiveSide = isMobile ? "bottom" : side

		const navigate = useNavigate()
		const [internalIsOpen, setInternalIsOpen] = useState(false)

		// Use external state if provided, otherwise use internal state
		const isPopoverOpen = externalIsOpen ?? internalIsOpen
		const setPopoverOpen = externalOnOpenChange ?? setInternalIsOpen
		const [isDialogOpen, setDialogOpen] = useState(false)
		const [searchQuery, setSearchQuery] = useState("")

		const ref = useRef<HTMLButtonElement>(null)

		const [filteredTags] = useQuery(
			queries.searchTags({ query: searchQuery, limit: 100 }),
		)
		const [tagGroups] = useQuery(queries.tagGroups())
		useShortcut("g + t", () => {
			void navigate({ to: "/settings/tags" })
		})
		useShortcut(
			"t",
			(e) => {
				e.preventDefault()
				ref.current?.focus()
				setPopoverOpen(true)
			},
			{
				enabled: isHotkeyEnabled && !isDisabled && role !== "VIEWER",
			},
		)

		const openDialog = React.useCallback(() => {
			setDialogOpen(true)
			setPopoverOpen(false)
		}, [setPopoverOpen])

		return (
			<>
				<CreateTagDialog
					title={searchQuery}
					isOpen={isDialogOpen}
					onOpenChange={(dialogOpen, newTag) => {
						if (!dialogOpen) {
							if (newTag) {
								onChange(newTag.id, newTag.color)
								setSearchQuery("")
								analytics.track("created-inline-issue")
							}

							setDialogOpen(false)
							analytics.track("close-inline-create-issue-dialog")
						}
					}}
				/>
				<Popover
					open={isPopoverOpen}
					onOpenChange={setPopoverOpen}
					modal={modal}
					onCloseAutoFocus={onCloseAutoFocus}
					className="p-0"
					content={
						<Command shouldFilter={false}>
							<div className="relative">
								<CommandInput
									autoFocus
									placeholder="Search or create tag..."
									value={searchQuery}
									onValueChange={setSearchQuery}
									className="max-w-[13rem]"
								/>
								<div className="absolute right-5 top-1/2 flex -translate-y-1/2 items-center gap-1">
									<div className="flex items-center gap-1.5">
										<Shortcut>T</Shortcut>
									</div>
								</div>
							</div>
							<CommandList>
								<CommandGroup>
									<AddNewItem
										filteredOpportunities={filteredTags}
										onCreate={openDialog}
									/>
								</CommandGroup>
								{tagGroups.map((tagGroup) => {
									const fTags = filteredTags.filter(
										(t) => t.tagGroupId === tagGroup.id,
									)
									if (fTags.length === 0) {
										return null
									}
									return (
										<CommandGroup key={tagGroup.id} value={tagGroup.id}>
											<DropdownMenuLabel style={{ color: tagGroup.color }}>
												{tagGroup.name}
											</DropdownMenuLabel>
											{fTags.map((tag) => {
												const TagIcon = getProjectIcon(tag.icon)
												return (
													<CommandItem
														key={tag.id}
														value={tag.id}
														onSelect={() => {
															onChange(tag.id, tagGroup.color)
															setSearchQuery("")
															if (closeOnSelect) {
																setPopoverOpen(false)
															}
														}}
													>
														<div className="flex items-center gap-2">
															<TagIcon
																className="h-3 w-3"
																style={{ color: tagGroup.color }}
															/>
															<span className="flex-1 truncate">
																{tag.name}
															</span>
															{tagIds.includes(tag.id) && (
																<Check className="h-4 w-4" />
															)}
														</div>
													</CommandItem>
												)
											})}
										</CommandGroup>
									)
								})}
							</CommandList>
						</Command>
					}
					side={responsiveSide}
					align={align}
				>
					{children ?? (
						<div>
							<Tooltip
								label={
									<div className="flex items-center gap-1">
										<span>Link or create tag</span>
										<Shortcut>{ShortcutSymbols.T}</Shortcut>
									</div>
								}
							>
								<Button
									Icon={TagFilledIcon}
									variant={variant}
									className="relative flex h-6 min-h-[1.75rem] max-w-xs flex-none select-none items-center justify-center gap-2 rounded-full px-2.5 text-xs font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-600 disabled:pointer-events-none"
									size="sm"
								>
									Tag
								</Button>
							</Tooltip>
						</div>
					)}
				</Popover>
			</>
		)
	},
)

interface AddNewItemProps {
	onCreate: (value: string) => void
	filteredOpportunities: Tag[]
}

const AddNewItem = React.memo(
	({
		onCreate,
		filteredOpportunities: _filteredOpportunities,
	}: AddNewItemProps) => {
		const search = useCommandState((state) => state.search).trim()

		return (
			<CommandAddNewItem
				onCreate={onCreate}
				show={true}
				search={search || "tag"}
				onAnalyticsTrack={() => {
					analytics.track("open-inline-create-opportunity-dialog")
				}}
			/>
		)
	},
)
